import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout/Layout';
import { toast } from 'react-toastify';
import Seo from '../../components/Seo';

import logo from '../../images/logo-fff.png';

import { useTranslation } from 'react-i18next';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const ProfilePage = () => {
    const { t } = useTranslation();
    const { user, updateUser } = useAuth();
    const [profileData, setProfileData] = useState({ 
        username: user?.username || '',
        full_name: user?.full_name || '',
        phone: user?.phone || ''
    });
    const [passwordData, setPasswordData] = useState({ old_password: '', new_password: '', confirm_password: '' });
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [profileLoading, setProfileLoading] = useState(false);

    React.useEffect(() => {
        const fetchUserData = async () => {
            if (!user?.id) return;
            try {
                const res = await fetch(`${API_URL}users.php?id=${user.id}`);
                const data = await res.json();
                if (data && !data.error) {
                    setProfileData({
                        username: data.username || '',
                        full_name: data.full_name || '',
                        phone: data.phone || ''
                    });
                    updateUser(data); // Sync local context
                }
            } catch (err) {
                console.error("Failed to fetch fresh user data:", err);
            }
        };
        fetchUserData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id]);

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setProfileLoading(true);
        try {
            const res = await fetch(`${API_URL}users.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'update_profile', id: user.id, ...profileData })
            });
            const result = await res.json();
            if (result.success) {
                updateUser({ 
                    username: profileData.username,
                    full_name: profileData.full_name,
                    phone: profileData.phone
                });
                toast.success(t('profile.updated') || "Profile updated successfully");
            } else {
                toast.error(result.error);
            }
        } catch (err) {
            toast.error("Connection error");
        }
        setProfileLoading(false);
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error("New passwords do not match");
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}users.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'change_password', id: user.id, ...passwordData })
            });
            const result = await res.json();
            if (result.success) {
                toast.success("Password updated successfully");
                setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
            } else {
                toast.error(result.error);
            }
        } catch (err) {
            toast.error("Connection error");
        }
        setLoading(false);
    };

    const handleProfilePicUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const data = new FormData();
        data.append('image', file);
        try {
            const res = await fetch(`${API_URL}upload.php`, { method: 'POST', body: data });
            const result = await res.json();
            if (result.success) {
                const updateRes = await fetch(`${API_URL}users.php`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ id: user.id, profile_picture: result.url })
                });
                if ((await updateRes.json()).success) {
                    updateUser({ profile_picture: result.url });
                    toast.success("Profile picture updated");
                }
            }
        } catch (err) {
            toast.error("Upload failed");
        }
    };

    const [activeAccordion, setActiveAccordion] = useState('personal');

    const toggleAccordion = (section) => {
        setActiveAccordion(activeAccordion === section ? null : section);
    };

    if (!user) return null;

    return (
        <Layout hideScrollbar>
            <Seo title={t('header.myProfile')} />
            <main className="page_content pt-150 pb-120 profile-page-container">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10">
                            <div className="profile-card item-details-widget p-0 overflow-hidden">
                                <div className="row g-0">
                                    {/* Sidebar/Profile Header */}
                                    <div className="col-lg-4 p-5 text-center profile-sidebar">
                                        <div className="profile-img-wrapper mb-4">
                                            <img 
                                                src={user.profile_picture ? (user.profile_picture.startsWith('http') ? user.profile_picture : API_URL + user.profile_picture) : logo} 
                                                alt="Profile" 
                                                className="profile-img"
                                                style={{ backgroundColor: user.profile_picture ? 'transparent' : '#1a1a24', padding: user.profile_picture ? '0' : '20px' }}
                                            />
                                            <label className="upload-btn">
                                                <i className="fas fa-camera"></i>
                                                <input type="file" onChange={handleProfilePicUpload} hidden accept="image/*" />
                                            </label>
                                        </div>
                                        <h2 className="text-white mb-2">{user.username}</h2>
                                        <span className="badge bg-primary text-uppercase px-3 py-2 rounded-pill mb-4">{user.role.replace('_', ' ')}</span>
                                        
                                        <div className="mt-4 pt-4 border-top border-white-10 text-start w-100">
                                            <p className="text-white-50 small mb-1">{t('profile.memberSince') || 'Member since'}</p>
                                            <p className="text-white mb-0 fw-bold">
                                                {user.created_at ? new Date(user.created_at).toLocaleDateString() : (t('common.unknown') || 'N/A')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Accordion Sections */}
                                    <div className="col-lg-8 p-5">
                                        <div className="accordion-container">
                                            {/* Personal Information Accordion */}
                                            <div className={`accordion-item-modern ${activeAccordion === 'personal' ? 'active' : ''} mb-4`}>
                                                <div className="accordion-header-modern" onClick={() => toggleAccordion('personal')}>
                                                    <h4 className="text-white mb-0 d-flex align-items-center gap-3">
                                                        <i className="fas fa-user-edit text-primary"></i>
                                                        {t('profile.personalInfo') || 'Personal Information'}
                                                    </h4>
                                                    <i className={`fas fa-chevron-${activeAccordion === 'personal' ? 'up' : 'down'} text-white-50`}></i>
                                                </div>
                                                <div className="accordion-content-modern">
                                                    <form onSubmit={handleProfileUpdate} className="pt-4">
                                                        <div className="mb-4">
                                                            <label className="text-white-50 small mb-2">{t('profile.fullName') || 'Full Name'}</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control-modern" 
                                                                value={profileData.full_name}
                                                                onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                                                                placeholder="John Doe"
                                                            />
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className="text-white-50 small mb-2">{t('profile.phone') || 'Phone Number'}</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control-modern" 
                                                                value={profileData.phone}
                                                                onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                                                                placeholder="+995 ..."
                                                            />
                                                        </div>
                                                        <div className="mb-4">
                                                            <label className="text-white-50 small mb-2">{t('profile.username') || 'Username'}</label>
                                                            <input 
                                                                type="text" 
                                                                className="form-control-modern" 
                                                                value={profileData.username}
                                                                onChange={(e) => setProfileData({...profileData, username: e.target.value})}
                                                                required 
                                                            />
                                                        </div>
                                                        <button type="submit" className="btn-save px-5" disabled={profileLoading}>
                                                            {profileLoading ? (t('common.saving') || 'Saving...') : (t('common.saveChanges') || 'Save Changes')}
                                                        </button>
                                                    </form>
                                                </div>
                                            </div>

                                            {/* Security Settings Accordion */}
                                            <div className={`accordion-item-modern ${activeAccordion === 'security' ? 'active' : ''}`}>
                                                <div className="accordion-header-modern" onClick={() => toggleAccordion('security')}>
                                                    <h4 className="text-white mb-0 d-flex align-items-center gap-3">
                                                        <i className="fas fa-shield-alt text-primary"></i>
                                                        {t('profile.security') || 'Security Settings'}
                                                    </h4>
                                                    <i className={`fas fa-chevron-${activeAccordion === 'security' ? 'up' : 'down'} text-white-50`}></i>
                                                </div>
                                                <div className="accordion-content-modern">
                                                    <form onSubmit={handlePasswordChange} className="pt-4">
                                                        <div className="mb-4">
                                                            <label className="text-white-50 small mb-2">{t('profile.currentPass') || 'Current Password'}</label>
                                                            <div className="password-input-wrapper">
                                                                <input 
                                                                    type={showOldPassword ? 'text' : 'password'} 
                                                                    className="form-control-modern" 
                                                                    value={passwordData.old_password}
                                                                    onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                                                                    required 
                                                                />
                                                                <button type="button" className="password-toggle" onClick={() => setShowOldPassword(!showOldPassword)} tabIndex="-1">
                                                                    <i className={`fas ${showOldPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className="row">
                                                            <div className="col-md-6 mb-4">
                                                                <label className="text-white-50 small mb-2">{t('profile.newPass') || 'New Password'}</label>
                                                                <div className="password-input-wrapper">
                                                                    <input 
                                                                        type={showNewPassword ? 'text' : 'password'} 
                                                                        className="form-control-modern" 
                                                                        value={passwordData.new_password}
                                                                        onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                                                                        required 
                                                                    />
                                                                    <button type="button" className="password-toggle" onClick={() => setShowNewPassword(!showNewPassword)} tabIndex="-1">
                                                                        <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                            <div className="col-md-6 mb-4">
                                                                <label className="text-white-50 small mb-2">{t('profile.confirmPass') || 'Confirm New Password'}</label>
                                                                <div className="password-input-wrapper">
                                                                    <input 
                                                                        type={showConfirmPassword ? 'text' : 'password'} 
                                                                        className="form-control-modern" 
                                                                        value={passwordData.confirm_password}
                                                                        onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                                                                        required 
                                                                    />
                                                                    <button type="button" className="password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)} tabIndex="-1">
                                                                        <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <button type="submit" className="btn-save w-100" disabled={loading}>
                                                            {loading ? (t('common.updating') || 'Updating...') : (t('profile.changePass') || 'Update Password')}
                                                        </button>
                                                    </form>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <style>{`
                .profile-card { border-radius: 30px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); }
                .profile-sidebar { background: rgba(108, 92, 231, 0.03); border-right: 1px solid rgba(255,255,255,0.06); }
                .profile-img-wrapper { position: relative; width: 160px; height: 160px; margin: 0 auto; }
                .profile-img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; border: 4px solid #6c5ce7; box-shadow: 0 0 30px rgba(108, 92, 231, 0.2); }
                .upload-btn { position: absolute; bottom: 5px; right: 5px; background: #6c5ce7; color: #fff; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.3s; box-shadow: 0 4px 10px rgba(0,0,0,0.3); }
                .upload-btn:hover { transform: scale(1.1); background: #5a4ad1; }
                
                .accordion-item-modern { border-bottom: 1px solid rgba(255,255,255,0.06); }
                .accordion-header-modern { padding: 15px 0; display: flex; justify-content: space-between; align-items: center; cursor: pointer; }
                .accordion-content-modern { max-height: 0; overflow: hidden; transition: max-height 0.4s ease-out, opacity 0.3s; opacity: 0; }
                .accordion-item-modern.active .accordion-content-modern { max-height: 800px; opacity: 1; padding-bottom: 30px; }
                
                .form-control-modern { background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); color: #fff; width: 100%; padding: 12px 20px; border-radius: 12px; transition: 0.3s; }
                .form-control-modern:focus { border-color: #6c5ce7; background: rgba(255,255,255,0.08); outline: none; }
                .password-input-wrapper { position: relative; }
                .password-input-wrapper .form-control-modern { padding-right: 45px; }
                .password-toggle { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); background: none; border: none; color: rgba(255,255,255,0.4); cursor: pointer; padding: 0; font-size: 14px; transition: color 0.2s; }
                .password-toggle:hover { color: #fff; }
                .btn-save { background: #6c5ce7; color: #fff; border: none; padding: 14px; border-radius: 12px; font-weight: 700; cursor: pointer; transition: 0.3s; }
                .btn-save:hover:not(:disabled) { background: #5a4ad1; transform: translateY(-2px); box-shadow: 0 8px 20px rgba(108, 92, 231, 0.3); }
                .border-white-10 { border-color: rgba(255,255,255,0.1) !important; }
            `}</style>
        </Layout>
    );
};

export default ProfilePage;
